import { getSchemaForType } from './dataSchemas';

import { 
  ExtractionStatus, 
  ExtractionProgress, 
  DataTypeStatus 
} from '../../types/chat';

export interface ExtractionResult {
  type: string;
  data: any;
  confidence: number;
}

export interface ExtractionResult {
  type: string;
  data: any;
  confidence: number;
}

export type ProgressCallback = (status: ExtractionStatus, progress?: ExtractionProgress) => void;

export class DataExtractionRouter {
  private extractors: Map<string, (content: string, entity?: string) => Promise<any>>;

  private progressCallback?: ProgressCallback;

  constructor() {
    this.extractors = new Map([
      ['overview', this.extractLocations],
      ['recommendation', this.extractLocations],
      ['flight', this.extractFlights],
      ['visa', this.extractVisa],
      ['budget', this.extractBudget],
      ['weather', this.extractWeather],
      ['festival', this.extractFestivals],
      ['currency', this.extractCurrency],
      ['language', this.extractLanguage],
      ['advisory', this.extractAdvisory],
      ['restaurant', this.extractRestaurants]
    ]);
  }

  // Add method to set progress callback
  setProgressCallback(callback: ProgressCallback) {
    this.progressCallback = callback;
  }

  // Helper to report progress
  private reportProgress(status: ExtractionStatus, progress?: ExtractionProgress) {
    if (this.progressCallback) {
      this.progressCallback(status, progress);
    }
  }

  async analyzeAndExtract(content: string, dataType?: string): Promise<ExtractionResult[]> {
    console.log('[DataExtractionRouter] Analyzing content for data extraction...');

    // Report detecting status
    this.reportProgress(ExtractionStatus.DETECTING);
    
    // Determine what types of data to extract
    //const dataTypes = await this.detectDataTypes(content);
    let dataTypes: {type: string, confidence: number, entity?: string, locationType?: string, country?: string}[];


    // If dataType provided, use it directly
    if (dataType) {
        dataTypes = [{type: dataType, confidence: 1.0}];
    } else {
        // Only detect if not provided
        dataTypes = await this.detectDataTypes(content);
    }

    console.log('[DataExtractionRouter] Detected data types:', dataTypes);

    // Initialize progress tracking
    const progress: ExtractionProgress = {};
    
    // Set up initial progress for detected types
    dataTypes.forEach(dt => {
      const key = dt.type === 'recommendation' || dt.type === 'overview' ? 'locations' : dt.type;
      if (!progress[key as keyof ExtractionProgress]) {
        progress[key as keyof ExtractionProgress] = {
          status: DataTypeStatus.PENDING,
          message: `Found ${key} data to extract`,
          count: 0
        };
      }
    });
    

    // Report extraction starting
    this.reportProgress(ExtractionStatus.EXTRACTING, progress);

    // Extract data for each detected type
    const results: ExtractionResult[] = [];
    
    // Add interface
    interface TypeGroup {
      type: string;
      confidence: number;
      entities: Array<{
        name: string;
        locationType?: string;
        country?: string;
      }>;
    }
    
    // Group by type first
    const typeGroups = dataTypes.reduce<Record<string, TypeGroup>>((acc, dt) => {
    if (!acc[dt.type]) {
        acc[dt.type] = { type: dt.type, confidence: dt.confidence, entities: [] };
    }
    if (dt.entity) {
        acc[dt.type].entities.push({
          name: dt.entity,
          locationType: dt.locationType,
          country: dt.country  // ← ADD THIS!
        });
    }
    return acc;
    }, {});

    // Process each type ONCE
    for (const group of Object.values(typeGroups)) {
    if (this.extractors.has(group.type)) {
        try {

        // Update progress for this type
        const progressKey = group.type === 'recommendation' || group.type === 'overview' ? 'locations' : group.type;
        progress[progressKey] = {
          status: DataTypeStatus.PROCESSING,
          message: `Extracting ${progressKey} information...`,
          count: group.entities.length || 1
        };
        
        this.reportProgress(ExtractionStatus.EXTRACTING, progress);

        const extractor = this.extractors.get(group.type)!;

        console.log("DATA EXTRACTION FUNCTIONS TO BE CALLED ------ ", extractor);

        console.log("DATA EXTRACTION ENTITIES ARE ----- ", group.entities);

        const data = await extractor.call(
            this, 
            content, 
            group.entities
            //group.entities.length === 1 ? group.entities[0] : group.entities
        );
        

        console.log (" DATA EXTRACTION ROUTER IS ----- ",data);

        if (data) {
            // Update to enriching if it's a type that needs API calls
            console.log("DATA EXTRACTION ROUTER IS -----",['recommendation', 'overview', 'restaurant', 'flight'].includes(group.type));
            if (['recommendation', 'overview', 'restaurant', 'flight'].includes(group.type)) {
              progress[progressKey] = {
                status: DataTypeStatus.PROCESSING,
                message: `Enriching ${progressKey} with additional details...`,
                count: Array.isArray(data) ? data.length : 1
              };
              this.reportProgress(ExtractionStatus.ENRICHING, progress);
            }
            results.push({
            type: group.type,
            data,
            confidence: group.confidence
            });

            // Mark as complete
            progress[progressKey] = {
              status: DataTypeStatus.COMPLETE,
              message: `Found ${Array.isArray(data) ? data.length : 1} ${progressKey}`,
              count: Array.isArray(data) ? data.length : 1
            };

        }
        } catch (error) {
          console.error(`[DataExtractionRouter] Error extracting ${group.type}:`, error);
          const progressKey = group.type === 'recommendation' || group.type === 'overview' ? 'locations' : group.type;
          progress[progressKey] = {
            status: DataTypeStatus.ERROR,
            message: `Failed to extract ${progressKey}`,
            count: 0,
            error: error.message
          };
        }

      this.reportProgress(ExtractionStatus.EXTRACTING, progress);

    }
    }

    // Report rendering status before returning
    if (results.length > 0) {
      this.reportProgress(ExtractionStatus.RENDERING, progress);
    }

    return results;
  }

  public async runDataTypeTests(): Promise<void> {
    const testCases = [
        {
            name: "Tokyo Overview",
            content: "Tokyo is Japan's bustling capital city. The metropolitan area houses over 37 million people, making it the world's most populous metropolis. Notable areas include Shibuya with its famous crossing, the historic Asakusa district, and modern Roppongi Hills.",
            expected: "locations (overview)"
        },
        {
            name: "Restaurant Mix",
            content: "For authentic sushi in Tokyo, Sukiyabashi Jiro offers an unforgettable omakase experience. The tiny 10-seat restaurant in Ginza has earned three Michelin stars. Meanwhile, Narisawa provides innovative French-Japanese fusion in a more relaxed setting.",
            expected: "restaurant"
        },
        {
            name: "Flight Info",
            content: "Your connection looks tight - United UA837 arrives at Tokyo Narita Terminal 1 at 14:30, and your ANA NH203 to Osaka departs from Terminal 2 at 16:45. The inter-terminal shuttle takes about 15 minutes.",
            expected: "flight"
        },
        {
            name: "Budget Breakdown",
            content: "For a week in Japan, budget travelers can manage on ¥8,000-10,000 per day. This breaks down to roughly ¥3,000 for accommodation, ¥2,500 for food, ¥1,500 for transport, and ¥2,000 for attractions and activities.",
            expected: "budget"
        },
        {
            name: "Visa Rules",
            content: "US citizens can enter Japan visa-free for up to 90 days. You'll need to show proof of onward travel and may be asked about accommodation. The immigration form requires your hotel address, so have it ready.",
            expected: "visa"
        },
        {
            name: "Weather Pattern",
            content: "Tokyo weather this week shows partly cloudy conditions with temperatures ranging from 18-24°C. Thursday expects light rain with 60% precipitation chance. Weekend looks clear with humidity around 65%.",
            expected: "weather"
        },
        {
            name: "Festival Info",
            content: "The Gion Matsuri runs throughout July in Kyoto, with the main parade on July 17th. Over 30 elaborate floats process through downtown, some weighing up to 12 tons. Evening festivities include traditional music and street food stalls.",
            expected: "festival"
        },
        {
            name: "Currency Exchange",
            content: "Current exchange shows 1 USD = 149.85 JPY. Most 7-Eleven ATMs accept foreign cards with better rates than airport exchanges. Banks typically charge 3-4% conversion fees.",
            expected: "currency"
        },
        {
            name: "Travel Advisory",
            content: "The US State Department maintains Japan at Level 1 - Exercise Normal Precautions. Be aware that typhoon season runs June through October. Register with STEP for emergency alerts.",
            expected: "advisory"
        },
        {
            name: "Language Phrases",
            content: "Essential Japanese phrases include 'Arigatou gozaimasu' (thank you very much), 'Sumimasen' (excuse me/sorry), and 'Eigo o hanasemasu ka?' (Do you speak English?). Most signs in Tokyo stations have English.",
            expected: "language"
        }
    ];

    console.log('=== DATA TYPE DETECTION TESTS ===');
    
    for (const test of testCases) {
        const result = await this.detectDataTypes(test.content);
        console.log(`\nTest: ${test.name}`);
        console.log(`Expected: ${test.expected}`);
        console.log(`Result:`, result);
        console.log(`✓ Pass:`, result.length > 0 && result[0].type === test.expected.split(' ')[0]);
    }
    }

  /* Rules:
        - context="overview": General description of a SINGLE place/country/city. Extract the main place in "entity" field
        - context="recommendation": ONLY when text explicitly says "must visit", "top places", "best", "recommended", OR uses numbered/bulleted format with specific ${LOCATION_TYPES}
        - For ANY overview text, return EXACTLY ONE entry with the MAIN place being described
        - context="overview": When text says "X is a city/country..." - extract ONLY X in entity field
        - context="recommendation": ONLY when text has phrases like "must visit", "top X places to", "best", "recommended" followed by numbered/bulleted lists
        - Do NOT extract secondary locations mentioned within overviews (neighborhoods, landmarks, etc)
        - Do NOT mark descriptive content as recommendations
        - locations: Extract ONLY if context="recommendation" OR context="overview"
        - Mentions of attractions within an overview (like "Key attractions include...") are still context="overview", NOT recommendations
        - restaurant: Extract ONLY for detailed dining recommendations with names and descriptions
        - flight: Extract ONLY with specific flight numbers, times, or airline details
        - visa: Extract ONLY with specific requirements or procedures
        - budget: Extract ONLY with actual costs or price breakdowns
        - weather: Extract ONLY with specific conditions or forecasts
        - Only include items with confidence > 0.7
        - For casual mentions, set context="mention", confidence=0.3 (will be excluded)
        - Return [] if no high-confidence structured data found

        Examples:
        - "Amsterdam is the capital city... canal ring area... Royal Palace..." → [{"type":"locations","confidence":0.9,"context":"overview","entity":"Amsterdam"}]
        - "Bolivia is a country... Lake Titicaca... Salar de Uyuni..." → [{"type":"locations","confidence":0.9,"context":"overview","entity":"Bolivia"}]
        - "Top 3 places to visit: 1. Museum 2. Park" → [{"type":"locations","confidence":0.95,"context":"recommendation"}]
        - "Bolivia is a landlocked country..." → [{"type":"locations","confidence":0.9,"context":"overview","entity":"Bolivia"}]
        - "Mumbai is India's largest city. Key attractions include Gateway of India" → [{"type":"locations","confidence":0.9,"context":"overview","entity":"Mumbai"}]
        - "Top 3 must-visit places in Mumbai: 1. Gateway 2. Marine Drive" → [{"type":"locations","confidence":0.95,"context":"recommendation","entity":"Mumbai"}]

        Do NOT create multiple entries or invent new types EVER.
        Do NOT include partial JSON or explanatory text EVER. */

        /*  - If an 'overview' type is detected once in the stop, immediately stop all further detection and return a completed JSON just contains only a single entity with ONLY one main place that is described, ignore all sub-locations/attractions or any other types
          - THIS OUTPUT IS WRONG - [{ "type": "overview","confidence": 0.9,"entity": "Minca"},{"type": "recommendation","confidence": 0.95,"entity": "waterfalls"},{"type": "recommendation","confidence": 0.95,"entity": "hiking trails"}]
          BECAUSE IT CONTAINS TYPES "overview" AND "recommendation" IN THE SAME ARRAY !! 
          - THIS IS CORRECT - [{ "type": "overview","confidence": 0.9,"entity": "Minca"}]
          BECAUSE IT CONTAINS ONLY TYPE "overview" IN THE ARRAY AND NO OTHER TYPE OR ELEMENT IN THE ARRAY!! 
          - THIS RULE CANNOT BE OVERRIDDEN FOR TYPE 'overview', IT IS CRITICAL FOR DATA INTEGRITY. 

          - If an 'overview' type is detected once, immediately stop all further detection and return a completed JSON that just contains only a single entity with ONLY one main place that is described, ignore all sub-locations/attractions or any other types
          
          */

        /* PRIORITY RULES:
          - If contains a numbered or bulleted list of restaurants/dining/eateries/street food or ANY food establishments → type should be classified as "restaurant" (NOT recommendation)
          - Restaurant takes precedence over recommendation when both could apply when content mentions specific restaurants or dining recommendations
          - For recommendations/overview, detect the specific location subtype from the list above
          - Include "locationType" field with the matching subtype
          - Example: "Perito Moreno Glacier" → [{"type":"recommendation","confidence":0.95,"entity":"Perito Moreno Glacier","locationType":"glacier"}]
          - Example: "Visit the Natural History Museum" → [{"type":"recommendation","confidence":0.95,"entity":"Natural History Museum","locationType":"museum"}]
          - Example: "Bergen is a city..." → [{"type":"overview","confidence":0.9,"entity":"Bergen"},{"type":"recommendation","confidence":0.9,"entity":"Bergen","locationType":"city"}]
          - Example: "The Masai Mara National Reserve in Kenya" → 
            [
              {"type":"overview","confidence":0.95,"entity":"Masai Mara"},
              {"type":"overview","confidence":0.95,"entity":"Masai Mara","locationType":"reserves"}
            ]

          SINGLE ENTITY TYPES (return ONE item):
          - overview: ONE place being described
          Example: "Mumbai is a city..." → [{"type":"overview","confidence":0.9,"entity":"Mumbai"},{"type":"recommendation","confidence":0.9,"entity":"Mumbai","locationType":"city"}]
          - weather: Weather for ONE location
          Example: "Weather in Tokyo..." → [{"type":"weather","confidence":0.9,"entity":"Tokyo"}]
          - visa: Visa for ONE country
          Example: "Japan visa requires..." → [{"type":"visa","confidence":0.9,"entity":"Japan"}]

          CRITICAL RULES FOR TYPE "overview":
          - If the content contains words such as "overview", "description", "general information" or similar phrases, it should be classified as 'overview'.
          - If an 'overview' type is detected, immediately return exactly TWO objects:
            1. The overview entity (the main place being described) - NO locationType field
            2. A recommendation object with the SAME entity and include locationType field
            
          Both objects MUST reference the SAME main location. Do not include any sub-locations, attractions, or additional entities.

          Example for overview detection:
          Input: "The Masai Mara National Reserve in Kenya is one of Africa's most magnificent game reserves..."

          Output:
          [
            {
              "type": "overview",
              "confidence": 0.95,
              "entity": "Masai Mara National Reserve"
            },
            {
              "type": "recommendation",
              "confidence": 0.95,
              "entity": "Masai Mara National Reserve",
              "locationType": "national park"
            }
          ]

          CORRECT OUTPUT: Contains exactly 2 objects - one overview and one recommendation for the SAME entity

          WRONG OUTPUT: 
          [
            {"type": "overview", "entity": "Minca"},
            {"type": "recommendation", "entity": "waterfalls", "locationType": "waterfall"},
            {"type": "recommendation", "entity": "hiking trails", "locationType": "trail"}
          ]
          This is WRONG because it includes different entities instead of repeating the main location

          WRONG OUTPUT:
          [{"type": "overview", "confidence": 0.9, "entity": "Minca"}]
          This is WRONG because it's missing the required recommendation object

          THIS RULE CANNOT BE OVERRIDDEN FOR TYPE 'overview', IT IS CRITICAL FOR DATA INTEGRITY.
 */


  private async detectDataTypes(content: string): Promise<{type: string, confidence: number, entity?: string}[]> {
    try {
        console.log('[DataExtractionRouter] Detecting data types in content...', content);
        
        const LOCATION_TYPES = 'museums, art galleries, restaurants, cafes, bars, nightclubs, hotels, hostels, resorts, parks, gardens, beaches, monuments, temples, churches, mosques, synagogues, palaces, castles, forts, bridges, towers, viewpoints, markets, shopping malls, stores, theaters, cinemas, concert halls, stadiums, amusement parks, theme parks, zoos, aquariums, observatories, planetariums, libraries, universities, historic sites, archaeological sites, memorials, cemeteries, spas, casinos, golf courses, ski resorts, marinas, piers, lighthouses, waterfalls, caves, mountains, lakes, islands, vineyards, wineries, breweries, distilleries, farms, ranches, botanical gardens, national parks, nature reserves, hiking trails, adventure sports, cultural centers, festival grounds, streets, squares, neighborhoods, districts';
        
        const response = await fetch('/api/analyze-content', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
        content,
        prompt: `Return JSON array for data type detection only:
          Valid types: overview|recommendation|restaurant|flight|visa|budget|weather|festival|currency|language|advisory

          LOCATION SUBTYPES: ${LOCATION_TYPES}

          CRITICAL RULES FOR TYPE "overview":
          - If content describes a SINGLE place/city/country (e.g., "X is a city...", "X is known for...", "Tell me about X"), classify as 'overview'
          - When overview is detected, IMMEDIATELY STOP all further extraction and return ONLY:
            1. One overview object for the MAIN place (NO locationType field)
            2. One recommendation object for the SAME place (WITH locationType field)

          STRICT EXTRACTION RULES:
          - Extract ONLY the MAIN place being described (e.g., "Mumbai" from "Mumbai is a city...")
          - IGNORE all secondary locations mentioned within the overview:
            • Neighborhoods (e.g., "Bandra", "Juhu" within Mumbai description)
            • Landmarks (e.g., "Gateway of India", "Marine Drive")
            • Attractions (e.g., "museums", "temples", "beaches")
          - Phrases like "Key attractions include..." are still overview, NOT recommendations
          - Do NOT extract any places mentioned as examples or comparisons
          - "For any location, infer the country from context:
            - 'Swiss Alps' → country: 'Switzerland' 
            - 'Canadian Rockies' → country: 'Canada'
            - 'Great Barrier Reef' → country: 'Australia'
            

          Example:
          Input: "Mumbai is India's largest city. Key attractions include Gateway of India, Marine Drive, and Chhatrapati Shivaji Terminus. The city has vibrant neighborhoods like Bandra and Juhu."

          CORRECT Output:
          [
            {
              "type": "overview",
              "confidence": 0.95,
              "entity": "Mumbai",
              "country": "India"
            },
            {
              "type": "recommendation",
              "confidence": 0.95,
              "entity": "Mumbai",
              "locationType": "city",
              "country": "India"
            }
          ]

          WRONG Output (extracts secondary locations):
          [
            {"type": "overview", "confidence": 0.95, "entity": "Mumbai"},
            {"type": "recommendation", "confidence": 0.95, "entity": "Mumbai", "locationType": "city", "country": "India"},
            {"type": "recommendation", "confidence": 0.95, "entity": "Gateway of India", "locationType": "monument", "country": "India"},
            {"type": "recommendation", "confidence": 0.95, "entity": "Marine Drive", "locationType": "street", "country": "India"}
          ]

          THIS RULE CANNOT BE OVERRIDDEN - Overview means ONLY the main place, nothing else.


          CRITICAL RULES FOR TYPE "recommendations":
          - ONLY for non-restaurant places (${LOCATION_TYPES} minus restaurant/cafe/bar)
          - Include "locationType" field with the specific type from LOCATION_TYPES
          - If no exact match, use closest category or "place" as default
          - Example: "Perito Moreno Glacier" → [{"type":"recommendation","confidence":0.95,"entity":"Perito Moreno Glacier","locationType":"glacier"}]
          - If the content contains words such as "recommendations", "top 5 places", or similar phrases and a numbered or bulleted list of recommendations, it should be classified as 'recommendation'.
          - If text contains SPECIFIC RECOMMENDATIONS as numbered/bulleted list of recommended places (${LOCATION_TYPES}), extract those recommended places
          - If restaurants are mixed with other places, separate them:
            Example: "Visit Gateway of India and try Britannia restaurant" → [{"type":"recommendation","confidence":0.95,"entity":"Gateway of India", "country": "India"},{"type":"restaurant","confidence":0.95,"entity":"Britannia", "country": "India"}]
          - Extract ONLY the MAIN numbered items (1., 2., 3., etc.) or main bulleted items
          - Example: "1. Lofoten Islands... visit Reine village..." → Extract ONLY "Lofoten Islands"
          - Example: "5. Bergen... Bryggen wharf... Mount Fløyen..." → Extract ONLY "Bergen"
          - DO NOT extract any additional places mentioned WITHIN the descriptions

          CRITICAL RULES FOR TYPE "restaurant":
          - If the content contains words such as "top 5 restaurants", "best dining recommendations", or similar phrases, and a numbered or bulleted list of restaurants, it should be classified as 'restaurant'.
          - ANY mention of restaurants, cafes, dining spots → type: "restaurant"
          - Examples: "best restaurants", "top dining", "where to eat", "food recommendations"
          - This takes PRIORITY over "recommendation" type
          - Always use type: "restaurant" (not recommendation)
          - Include "cuisine" field if mentioned
          - Extract restaurant name AND cuisine type if mentioned
          - Example: "Top 5 restaurants in Munich" → [{"type":"restaurant","confidence":0.95,"entity":"Restaurant Name","cuisine":"German"}]
          - Example: "1. Woodside Inn for British food" → [{"type":"restaurant","confidence":0.95,"entity":"Woodside Inn","cuisine":"British"}]
          - Example: "Try Britannia & Co. (Parsi cuisine)" → [{"type":"restaurant","confidence":0.95,"entity":"Britannia & Co.","cuisine":"Parsi"}]
          - Extract ONLY the MAIN titles mentioned in numbered items (1., 2., 3., etc.) or main bulleted items
          - Example: "1. Jai Hind Lunch Home (Marine Lines)... visit Mumbai..." → Extract ONLY restaurant name "Jai Hind Lunch Home"
          - Example: "3. Khyber (Marine Lines)..." → Extract ONLY "Khyber"
          - DO NOT extract any other additional places mentioned WITHIN the descriptions

          MULTIPLE ENTITY TYPES EXAMPLES (can return multiple items):
          - recommendation: Multiple places listed
          Example: "Visit Gateway of India, Marine Drive" → [{"type":"recommendation","confidence":0.95,"entity":"Gateway of India"},{"type":"recommendation","confidence":0.95,"entity":"Marine Drive"}]
          - restaurant: Multiple restaurants
          Example: "Try Masala Library and Britannia" → [{"type":"restaurant","confidence":0.95,"entity":"Masala Library"},{"type":"restaurant","confidence":0.95,"entity":"Britannia"}]

          CRITICAL RULES FOR MULTIPLE ENTITY TYPES:
          - If 'recommendation' type is detected, return ALL places listed in the recommendation
          - If 'restaurant' type is detected, return ALL restaurants mentioned

          NO ENTITY TYPES (just type, no entity):
          - flight: Example: "Flight UA123..." → [{"type":"flight","confidence":0.9}]
          - budget: Example: "Hotel costs $200..." → [{"type":"budget","confidence":0.9}]
          - currency: Example: "1 USD = 150 JPY" → [{"type":"currency","confidence":0.9}]
          - language: Example: "Hello is Konnichiwa" → [{"type":"language","confidence":0.9}]
          - festival: Example: "Diwali on Nov 1" → [{"type":"festival","confidence":0.9}]
          - advisory: Example: "Level 2 warning" → [{"type":"advisory","confidence":0.9}]

          This is ONLY for detection. Full details extracted later.

          Text to analyze: ${content}`})
          });
        
        /* `Return JSON array for data type detection only:
          Valid types: overview|recommendation|restaurant|flight|visa|budget|weather|festival|currency|language|advisory

          LOCATION SUBTYPES: ${LOCATION_TYPES}

          PRIORITY RULES:
          - If contains a numbered or bulleted list of restaurants/dining/eateries/street food or ANY food establishments → type should be classified as "restaurant" (NOT recommendation)
          - Restaurant takes precedence over recommendation when both could apply when content mentions specific restaurants or dining recommendations
          - For recommendations/overview, detect the specific location subtype from the list above
          - Include "locationType" field with the matching subtype
          - Example: "Perito Moreno Glacier" → [{"type":"recommendation","confidence":0.95,"entity":"Perito Moreno Glacier","locationType":"glacier"}]
          - Example: "Visit the Natural History Museum" → [{"type":"recommendation","confidence":0.95,"entity":"Natural History Museum","locationType":"museum"}]
          - Example: "Bergen is a city..." → [{"type":"overview","confidence":0.9,"entity":"Bergen","locationType":"city"}]


          SINGLE ENTITY TYPES (return ONE item):
          - overview: ONE place being described
          Example: "Mumbai is a city..." → [{"type":"overview","confidence":0.9,"entity":"Mumbai"}]
          - weather: Weather for ONE location
          Example: "Weather in Tokyo..." → [{"type":"weather","confidence":0.9,"entity":"Tokyo"}]
          - visa: Visa for ONE country
          Example: "Japan visa requires..." → [{"type":"visa","confidence":0.9,"entity":"Japan"}]

          CRITICAL RULES FOR TYPE "overview":
          - If the content contains words such as "overview", "description", "general information" or similar phrases, it should be classified as 'overview'. 
          - If an 'overview' type is detected once in the stop, immediately stop all further detection and return a completed JSON just contains only a single entity with ONLY one main place that is described, ignore all sub-locations/attractions or any other types
          - THIS OUTPUT IS WRONG - [{ "type": "overview","confidence": 0.9,"entity": "Minca"},{"type": "recommendation","confidence": 0.95,"entity": "waterfalls"},{"type": "recommendation","confidence": 0.95,"entity": "hiking trails"}]
          BECAUSE IT CONTAINS TYPES "overview" AND "recommendation" IN THE SAME ARRAY !! 
          - THIS IS CORRECT - [{ "type": "overview","confidence": 0.9,"entity": "Minca"}]
          BECAUSE IT CONTAINS ONLY TYPE "overview" IN THE ARRAY AND NO OTHER TYPE OR ELEMENT IN THE ARRAY!! 
          - THIS RULE CANNOT BE OVERRIDDEN FOR TYPE 'overview', IT IS CRITICAL FOR DATA INTEGRITY.

          CRITICAL RULES FOR TYPE "recommendations":
          - ONLY for non-restaurant places (${LOCATION_TYPES} minus restaurant/cafe/bar)
          - For places/locations, include a subtype in entity field if mentioned
          - Include "locationType" field with the specific type from LOCATION_TYPES
          - If no exact match, use closest category or "place" as default
            Example: "Perito Moreno Glacier" → [{"type":"recommendation","confidence":0.95,"entity":"Perito Moreno Glacier","subtype":"glacier"}]
          - If the content contains words such as "recommendations", "top 5 places", or similar phrases and a numbered or bulleted list of recommendations, it should be classified as 'recommendation'.
          - If text contains SPECIFIC RECOMMENDATIONS as numbered/bulleted list of recommended places (${LOCATION_TYPES}), extract those recommended places
          - If restaurants are mixed with other places, separate them:
            Example: "Visit Gateway of India and try Britannia restaurant" → [{"type":"recommendation","confidence":0.95,"entity":"Gateway of India"},{"type":"restaurant","confidence":0.95,"entity":"Britannia"}]
          - Extract ONLY the MAIN numbered items (1., 2., 3., etc.) or main bulleted items
          - Example: "1. Lofoten Islands... visit Reine village..." → Extract ONLY "Lofoten Islands"
          - Example: "5. Bergen... Bryggen wharf... Mount Fløyen..." → Extract ONLY "Bergen"
          - DO NOT extract any additional places mentioned WITHIN the descriptions
    

          CRITICAL RULES FOR TYPE "restaurant":
          - If the content contains words such as "top 5 restaurants", "best dining recommendations", or similar phrases, and a numbered or bulleted list of restaurants, it should be classified as 'restaurant'.
          - ANY mention of restaurants, cafes, dining spots → type: "restaurant"
          - Examples: "best restaurants", "top dining", "where to eat", "food recommendations"
          - This takes PRIORITY over "recommendation" type
          - Example: "Top 5 restaurants in Munich" → [{"type":"restaurant","confidence":0.95,"entity":"Restaurant Name","cuisine":"German"}]
          - Extract ONLY the MAIN titles mentioned in numbered items (1., 2., 3., etc.) or main bulleted items
          - Example: "1. Jai Hind Lunch Home (Marine Lines)... visit Mumbai..." → Extract ONLY restaurant name "Jai Hind Lunch Home"
          - Example: "3. Khyber (Marine Lines)..." → Extract ONLY "Khyber"
          - Always use type: "restaurant" (not recommendation)
          - Include "cuisine" field if mentioned
          - Example: "1. Woodside Inn for British food" → [{"type":"restaurant","confidence":0.95,"entity":"Woodside Inn","cuisine":"British"}]
          - Extract restaurant name AND cuisine type if mentioned
          - Example: "1. Woodside Inn for British food" → [{"type":"restaurant","confidence":0.95,"entity":"Woodside Inn","cuisine":"British"}]
          - Example: "Try Britannia & Co. (Parsi cuisine)" → [{"type":"restaurant","confidence":0.95,"entity":"Britannia & Co.","cuisine":"Parsi"}]
          - DO NOT extract any other additional places mentioned WITHIN the descriptions
        

          MULTIPLE ENTITY TYPES EXAMPLES (can return multiple items):
          - recommendation: Multiple places listed
          Example: "Visit Gateway of India, Marine Drive" → [{"type":"recommendation","confidence":0.95,"entity":"Gateway of India"},{"type":"recommendation","confidence":0.95,"entity":"Marine Drive"}]
          - restaurant: Multiple restaurants
          Example: "Try Masala Library and Britannia" → [{"type":"restaurant","confidence":0.95,"entity":"Masala Library"},{"type":"restaurant","confidence":0.95,"entity":"Britannia"}]

          CRITICAL RULES FOR MULTIPLE ENTITY TYPES:
          - If 'recommendation' type is detected, return ALL places listed in the recommendation
          - If 'restaurant' type is detected, return ALL restaurants mentioned


          NO ENTITY TYPES (just type, no entity):
          - flight: Example: "Flight UA123..." → [{"type":"flight","confidence":0.9}]
          - budget: Example: "Hotel costs $200..." → [{"type":"budget","confidence":0.9}]
          - currency: Example: "1 USD = 150 JPY" → [{"type":"currency","confidence":0.9}]
          - language: Example: "Hello is Konnichiwa" → [{"type":"language","confidence":0.9}]
          - festival: Example: "Diwali on Nov 1" → [{"type":"festival","confidence":0.9}]
          - advisory: Example: "Level 2 warning" → [{"type":"advisory","confidence":0.9}]

          This is ONLY for detection. Full details extracted later.

          Text to analyze: ${content}` */
        
        const result = await response.json();
        const text = result.content?.[0]?.text || '[]';
        console.log('[DataExtractionRouter] Raw API response:', text);
        
        const jsonMatch = text.match(/\[.*\]/s);
        if (jsonMatch) {
            const detectedTypes = JSON.parse(jsonMatch[0]);
            const filtered = detectedTypes
            .filter(item => item.confidence > 0.7)
            .reduce((acc, curr) => {
                // Single entity types - keep only highest confidence
                const singleEntityTypes = ['overview', 'weather', 'visa'];
                
                if (singleEntityTypes.includes(curr.type)) {
                const existing = acc.find(item => item.type === curr.type);
                if (!existing || curr.confidence > existing.confidence) {
                    return [...acc.filter(item => item.type !== curr.type), curr];
                }
                return acc;
                }
                
                // Multiple entity types - keep all items
                return [...acc, curr];
            }, []);

            console.log('[DataExtractionRouter] Filtered types:', filtered);
            return filtered;
        }
        return [];
        
    } catch (error) {
        console.error('[DataExtractionRouter] Error detecting data types:', error);
        return [];
    }
  }

/*   private async extractLocations_OG(content: string, detectedEntities?: string | string[]): Promise<any> {
    console.log('[DataExtractionRouter] extractLocations START', content, detectedEntities);
    
    // Extract context
    const contextResponse = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'location-context',
        schema: '{"city": "string", "country": "string", "locationType": "string"}',
        prompt: `Extract ONLY the common geographic context where these places are located.
                  DO NOT list all the places in the city field.

                  Examples:
                  - "Top places in Kerala: Munnar, Alleppey, Fort Kochi..." 
                    → {city: null, country: "India", locationType: "tourist destinations"}
                    
                  - "Best beaches in Kerala including Kovalam, Varkala..."
                    → {city: null, country: "India", locationType: "beaches"}
                    
                  - "Visit these Kerala cities: Munnar, Alleppey..."
                    → {city: null, country: "India", locationType: "cities"}

                  The city field should ONLY contain a single city name if all places are within that city.
                  Otherwise, leave it null.`
      })
    });
    
    const context = await contextResponse.json();

    // Convert to array if it's a string
    let entitiesArray = Array.isArray(detectedEntities) 
      ? detectedEntities 
      : (detectedEntities ? [detectedEntities] : []);
    
    // Filter out overview objects ONLY if multiple recommendations/restaurants exist
    if (entitiesArray.length > 0 && typeof entitiesArray[0] === 'object') {
      const recommendations = entitiesArray.filter(item => 
        item.type === 'recommendation' || item.type === 'restaurant'
      );
      
      // Only remove overview if there are 2 or more recommendations/restaurants
      if (recommendations.length >= 2) {
        entitiesArray = entitiesArray.filter(item => item.type !== 'overview');
      }
      
      // Extract just the entity names from the objects
      entitiesArray = entitiesArray.map(item => item.entity);
    }
    
    console.log('[DataExtractionRouter] Entities to process:', entitiesArray);
    
    if (entitiesArray.length > 0) {
      const placesResponse = await fetch('/api/places/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          places: entitiesArray.map(name => ({ name })),
          searchContext: context.data,
          type: context.data?.locationType || 'tourist_attraction'
        })
      });
      
      const result = await placesResponse.json();
      return result.success ? result.places : null;
    }

    return null;
  } */

  private async extractLocations(
      content: string, 
      detectedEntities?: string | string[] | Array<{name: string, locationType?: string, country?: string}>  // ← ADD country!
    ): Promise<any> {
    console.log('[DataExtractionRouter] extractLocations START', content, detectedEntities);
    
    // Extract context (unchanged)
    const contextResponse = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'location-context',
        schema: '{"city": "string", "country": "string", "locationType": "string"}',
        prompt: `Extract ONLY the common geographic context...`
      })
    });
    
    const context = await contextResponse.json();

    // Convert to array and normalize format
    let entitiesArray: Array<{name: string, locationType?: string, country?: string}> = [];
    
    if (Array.isArray(detectedEntities)) {
      // Check if it's old format (strings) or new format (objects)
      if (detectedEntities.length > 0 && typeof detectedEntities[0] === 'string') {
        // Old format: convert strings to objects
        entitiesArray = detectedEntities.map(name => ({ name }));
      } else {
        // New format: already objects
        entitiesArray = detectedEntities as Array<{name: string, locationType?: string}>;
      }
    } else if (typeof detectedEntities === 'string') {
      // Single string
      entitiesArray = [{ name: detectedEntities }];
    } else if (detectedEntities && typeof detectedEntities === 'object') {
      // Single object
      entitiesArray = [detectedEntities as {name: string, locationType?: string}];
    }
    
    console.log('[DataExtractionRouter] Entities to process:', entitiesArray);
    
    if (entitiesArray.length > 0) {
        const enrichedPlaces: any[] = []; // Add type here!
      
      // Individual calls for each location
      for (let i = 0; i < entitiesArray.length; i++) {
        const entity = entitiesArray[i];
        
        // Update progress for each location
        this.reportProgress(ExtractionStatus.ENRICHING, {
          locations: {
            status: DataTypeStatus.PROCESSING,
            message: `Finding ${entity.name}...`,
            count: i + 1          
          }
        });
        
        try {
          const response = await fetch('/api/places/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              places: [{
                name: entity.name,
                type: entity.locationType || 'place',
                country: entity.country
              }],
              searchContext: {},
              type: 'tourist_attraction'
            })
          });
          
          const result = await response.json();
          if (result.success && result.places) {
            enrichedPlaces.push(...result.places);
          }
        } catch (error) {
          console.error(`Failed to enrich ${entity.name}:`, error);
        }
      }
      
      // Only report complete ONCE at the end
      this.reportProgress(ExtractionStatus.COMPLETE, {
        locations: {
          status: DataTypeStatus.COMPLETE,
          message: `Found ${enrichedPlaces.length} locations`,
          count: enrichedPlaces.length        }
      });
      
      return enrichedPlaces;
    }
    /* if (entitiesArray.length > 0) {
      // Then pass ALL fields including country:
      const placesResponse = await fetch('/api/places/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          places: entitiesArray.map(entity => ({ 
            name: entity.name,
            type: entity.locationType || 'place',
            country: entity.country  // ← ADD THIS!
          })),
          searchContext: context.data,  // This is mostly useless now
          type: context.data?.locationType || 'tourist_attraction'
        })
      });
      
      const result = await placesResponse.json();
      return result.success ? result.places : null;
    } */

    return null;
  }

  private async extractLocations_UNUSED(content: string, detectedEntities?: string | string[]): Promise<any> {
    console.log('[DataExtractionRouter] extractLocations START', content, detectedEntities);
    
    // Extract context
    const contextResponse = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'location-context',
        schema: '{"city": "string", "country": "string", "locationType": "string"}',
        prompt: `Extract ONLY the common geographic context where these places are located.
                  DO NOT list all the places in the city field.

                  Examples:
                  - "Top places in Kerala: Munnar, Alleppey, Fort Kochi..." 
                    → {city: null, country: "India", locationType: "tourist destinations"}
                    
                  - "Best beaches in Kerala including Kovalam, Varkala..."
                    → {city: null, country: "India", locationType: "beaches"}
                    
                  - "Visit these Kerala cities: Munnar, Alleppey..."
                    → {city: null, country: "India", locationType: "cities"}

                  The city field should ONLY contain a single city name if all places are within that city.
                  Otherwise, leave it null.`
        //prompt: `Extract ONLY the city/country context and type of locations being discussed.`
      })
    });
    
    const context = await contextResponse.json();

    /* console.log('[DataExtractionRouter] Detected entities:', detectedEntities);
    
    if (detectedEntities && Array.isArray(detectedEntities)) {
      // PASS ENTITIES DIRECTLY - NO ENRICHRESULT
      console.log('[DataExtractionRouter] Detected entities:', detectedEntities);
      const placesResponse = await fetch('/api/places/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          places: detectedEntities.map(name => ({ name })), // <-- FIXED
          searchContext: context.data,
          type: context.data?.locationType || 'tourist_attraction'
        })
      });
      
      const result = await placesResponse.json();
      return result.success ? result.places : null;
    } */
    // Convert to array if it's a string
    const entitiesArray = Array.isArray(detectedEntities) 
      ? detectedEntities 
      : (detectedEntities ? [detectedEntities] : []);
    
    console.log('[DataExtractionRouter] Entities to process:', entitiesArray);
    
    if (entitiesArray.length > 0) {
      const placesResponse = await fetch('/api/places/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          places: entitiesArray.map(name => ({ name })),
          searchContext: context.data,
          type: context.data?.locationType || 'tourist_attraction'
        })
      });
      
      const result = await placesResponse.json();
      return result.success ? result.places : null;
    }

    return null; // Add this for when entitiesArray is empty

  }
  private async extractFlights_UNUSED1(content: string): Promise<any> {
    const schema = getSchemaForType('flight');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'flight',
        schema,
        prompt: `Extract flight information from this text. Include flight numbers, airlines, departure/arrival times, terminals, gates, and status. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }
  private async extractFlights_UNUSED2(content: string): Promise<any> {
    const schema = getSchemaForType('flight');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'flight',
        schema,
        prompt: `Extract flight information from this text. 
        Look for:
        - Flight numbers (e.g., UA123, AA 456, Delta 789)
        - Airlines and flight numbers separately
        - Route information (departure/arrival cities)
        - Any time or date mentions
        
        Return format:
        {
          "flightCode": "UA123",
          "airline": "United Airlines",
          "flightNumber": "123",
          "departure": "city name if mentioned",
          "arrival": "city name if mentioned",
          "date": "date if mentioned"
        }`
      })
    });
    
    const result = await response.json();
    return result.data;
  }

  private async extractFlights(content: string): Promise<any> {
    const extractionSchema = `{
      "flightNumber": "string",
      "airline": "string", 
      "flightCode": "string"
    }`;
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'flight',
        schema: extractionSchema,
        prompt: `Extract flight information from this text.
        
        CRITICAL: Use correct IATA airline codes:
        - Etihad = EY (NOT ETD, NOT EH, NOT ET)
        - Emirates = EK (NOT EM)
        - Qatar = QR
        - United = UA
        - American = AA
        - Delta = DL
        - Lufthansa = LH
        
        If user says "Etihad 826" or "Etihad flight 826":
        - flightNumber: "EY826"
        - airline: "Etihad Airways"
        - flightCode: "EY826"
        
        If user says "EY826":
        - flightNumber: "EY826"
        - airline: "Etihad Airways"
        - flightCode: "EY826"
        
        NEVER use ETD for Etihad. Always use EY.
        
        Text to analyze: ${content}`
      })
    });
    
    const extractResult = await response.json();
    
    if (!extractResult.success || !extractResult.data) {
      return null;
    }
    
    const flightIdentifiers = extractResult.data;
    
    // SAFETY CHECK: Fix common airline code mistakes
    if (flightIdentifiers.airline?.toLowerCase().includes('etihad') && 
        flightIdentifiers.flightCode?.startsWith('ETD')) {
      // Fix the code
      const flightNum = flightIdentifiers.flightCode.replace('ETD', '');
      flightIdentifiers.flightCode = `EY${flightNum}`;
      flightIdentifiers.flightNumber = `EY${flightNum}`;
    }
    
    console.log('[DataExtractionRouter] Extracted flight identifiers:', flightIdentifiers);
    
    // Step 2: Call flight status API (just like restaurants call places API)
    try {
      const flightResponse = await fetch('/api/flight-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(flightIdentifiers)
      });
      
      if (!flightResponse.ok) {
        console.error('[DataExtractionRouter] Flight API error:', flightResponse.status);
        return null;
      }
      
      const flightData = await flightResponse.json();
      console.log('[DataExtractionRouter] Flight status retrieved:', flightData);
      
      return flightData;
      
    } catch (error) {
      console.error('[DataExtractionRouter] Error fetching flight status:', error);
      return null;
    }
  }


  private async extractVisa(content: string): Promise<any> {
    const schema = getSchemaForType('visa');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'visa',
        schema,
        prompt: `Extract visa and travel document requirements from this text. Include visa types, costs, processing times, and requirements. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }

  private async extractBudget(content: string): Promise<any> {
    const schema = getSchemaForType('budget');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'budget',
        schema,
        prompt: `Extract budget and cost information from this text. Include total estimates, breakdown by category, and budget tips. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }

  private async extractWeather(content: string): Promise<any> {
    const schema = getSchemaForType('weather');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'weather',
        schema,
        prompt: `Extract weather and climate information from this text. Include current conditions, forecasts, and best times to visit. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }

  private async extractFestivals(content: string): Promise<any> {
    const schema = getSchemaForType('festival');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'festival',
        schema,
        prompt: `Extract festival and event information from this text. Include names, dates, locations, and highlights. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }

  private async extractCurrency(content: string): Promise<any> {
    const schema = getSchemaForType('currency');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'currency',
        schema,
        prompt: `Extract currency exchange information from this text. Include exchange rates, currency codes, and conversion amounts. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }

  private async extractLanguage(content: string): Promise<any> {
    const schema = getSchemaForType('language');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'language',
        schema,
        prompt: `Extract language and phrase information from this text. Include common phrases, pronunciations, and language tips. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }

  private async extractAdvisory(content: string): Promise<any> {
    const schema = getSchemaForType('advisory');
    
    const response = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'advisory',
        schema,
        prompt: `Extract travel advisory and safety information from this text. Include warning levels, specific risks, and emergency contacts. Follow the provided schema exactly.`
      })
    });
    
    const result = await response.json();
    return result.data;
  }


  private async extractRestaurants(content: string): Promise<any> {
    const schema = getSchemaForType('restaurant');

    // ONE EXTRACTION - GET EVERYTHING
    const extractResponse = await fetch('/api/extract-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify({
        content,
        type: 'restaurant-full',
        schema: '{"city": "string", "country": "string", "restaurants": [{"name": "string", "cuisine": "string"}]}',
        //schema,
        prompt: `Extract the city/country context AND all restaurant names with their cuisines.
         Return ONLY what's explicitly mentioned in the text.
         
         Schema: {"city": "string", "country": "string", "restaurants": [{"name": "string", "cuisine": "string"}]}
         
         Rules:
         - City/Country: The location context where restaurants are located
         - Name: Exact restaurant name as mentioned
         - Cuisine: Type of food (Italian, Japanese, Indian, Mexican, etc.) - extract if mentioned
         - If restaurants are in different cities, use the most common city or set city as null
         
         Examples:
          1. "Best restaurants in Mumbai include Britannia & Co. for Parsi food and Trishna for seafood"
            → {"city": "Mumbai", "country": "India", "restaurants": [
                {"name": "Britannia & Co.", "cuisine": "Parsi"},
                {"name": "Trishna", "cuisine": "Seafood"}
            ]}

          2. "Try Le Bernardin in NYC and French Laundry in Napa Valley"
            → {"city": null, "country": "USA", "restaurants": [
                {"name": "Le Bernardin", "cuisine": "French"},
                {"name": "French Laundry", "cuisine": "French"}
          ]}`
        /* `Extract the city/country context AND all restaurant names with their cuisines.
        Example: "Best restaurants in Mumbai include Britannia & Co. for Parsi food..."
        → {"city": "Mumbai", "country": "India", "restaurants": [{"name": "Britannia & Co.", "cuisine": "Parsi"}]}` */
      })
    });
    
    const result = await extractResponse.json();

    console.log('[DataExtractionRouter] Extracted restaurants:', result);

    console.log('[DataExtractionRouter] Extracted restaurants data:', result.success, result.data, result.data?.restaurants);
    
    if (result.success && result.data?.restaurants) {
      console.log('[DataExtractionRouter] Inside data extracted:', result.data.restaurants);
      // Google Places API call
      const placesResponse = await fetch('/api/places/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          places: result.data.restaurants.map(r => ({
            ...r,
            type: 'restaurant'  // ← ADD THIS for consistency
          })),//result.data.restaurants,
          searchContext: {
            city: result.data.city,
            country: result.data.country,
            locationType: 'restaurant'
          },
          type: 'restaurant'
        })
      });
      
      const placesResult = await placesResponse.json();

      console.log('[DataExtractionRouter] Place result response:', placesResult);

      if (placesResult.success) {
        // MAP to preserve cuisine from original extraction
        return placesResult.places.map(place => {
          // Find the original restaurant data to get cuisine
          const originalData = result.data.restaurants.find(
            r => r.name === place.name
          );
          
          return {
            ...place,
            cuisine: originalData?.cuisine || place.cuisine, // Preserve cuisine
            type: 'restaurant' // Ensure type is set
          };
        });
      }

      return placesResult.success ? placesResult.places : null;
    }
    
    return null;
  }
}
